import { FC, useState } from 'react';
import { Modal } from '../components/Modal';
import { Button } from '../components/Form';
import { useSorting } from '../hooks/useSorting';
import {
  useCreateReleaseNoteMutation,
  useGetReleaseNoteQuery,
  useGetReleaseNotesTableListQuery,
  useUpdateReleaseNoteMutation,
  useDeleteReleaseNoteMutation,
  IGetReleaseNotesTableItem,
} from '../api/api';
import { ReleaseNotesForm, ReleaseNotesFormData } from './release_notes/ReleaseNotesForm';
import { ReleaseNotesTable } from './release_notes/ReleaseNotesTable';

export const ReleaseNotes: FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReleaseNotesId, setSelectedReleaseNotesId] = useState<string | undefined>();
  const { data: releaseNotesItems, isLoading: isLoadingReleaseNotes } = useGetReleaseNotesTableListQuery();
  const { data: selectedReleaseNote, isLoading: isLoadingReleaseNote } = useGetReleaseNoteQuery(
    selectedReleaseNotesId ?? '',
    { skip: !selectedReleaseNotesId }
  );
  const [createReleaseNote] = useCreateReleaseNoteMutation();
  const [updateReleaseNote] = useUpdateReleaseNoteMutation();
  const [deleteReleaseNote] = useDeleteReleaseNoteMutation();

  const { items: sortedReleaseNotesItems, requestSort, sortConfig } = useSorting<IGetReleaseNotesTableItem>(
    releaseNotesItems || [],
    { key: 'publication_at', direction: 'desc' }
  );
  const releaseNoteForForm = selectedReleaseNotesId ? selectedReleaseNote : undefined;

  const handleSubmit = async (data: ReleaseNotesFormData) => {
    try {
      if (selectedReleaseNotesId) {
        await updateReleaseNote({ id: selectedReleaseNotesId, body: data });
      } else {
        await createReleaseNote(data);
      }

      setIsModalOpen(false);
      setSelectedReleaseNotesId(undefined);
    } catch (error) {
      console.error('Error saving release note:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedReleaseNotesId) {
        await deleteReleaseNote(selectedReleaseNotesId);
      }

      setIsModalOpen(false);
      setSelectedReleaseNotesId(undefined);
    } catch (error) {
      console.error('Error deleting release note:', error);
    }
  }

  if (isLoadingReleaseNotes) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Release Notes</h1>
        <Button
          onClick={() => {
            setSelectedReleaseNotesId(undefined);
            setIsModalOpen(true);
          }}
        >
          Create
        </Button>
      </div>

      <ReleaseNotesTable
        releaseNotesItems={sortedReleaseNotesItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        onRowClick={(id) => {
          setSelectedReleaseNotesId(id);
          setIsModalOpen(true);
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedReleaseNotesId(undefined);
        }}
        title={selectedReleaseNotesId ? 'Edit' : 'Create'}
      >
        {isLoadingReleaseNote ? (
          <div>Loading release note details...</div>
        ) : (
          <ReleaseNotesForm
            key={selectedReleaseNotesId ?? 'create-release-note'}
            releaseNote={releaseNoteForForm}
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsModalOpen(false);
              setSelectedReleaseNotesId(undefined);
            }}
            onDelete={handleDelete}
          />
        )}
      </Modal>
    </div>
  );
};
